import { useEffect, useRef, useState } from 'react'
import { Betrag } from './Betrag'
import { api, periodenname } from './api'
import { fachlicherSatz } from './Fehlersatz'
import {
  anzahlwort,
  belegerlaeuterung,
  belegtext,
  bewegungsartname,
  gesellschaftsname,
  herleitungsname,
  kontobezeichnung,
  ohneVortragszeilen,
  regelname,
  satz,
  t,
  zeitpunkt,
} from './sprache'
import type { Sprachschluessel } from './sprache'
import { ohneWert } from './Ohnewert'
import { schrittwert, vorgangsname } from './Vorgaenge'
import type { Belegkopf, Belegzeile, Buchungszeile, Rechenantwort, Vorgangskopf } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * Das Buchungsjournal — der Ort, an dem ein Beleg eingesehen wird (2.14).
 *
 * **Zwei Abschnitte, und der Unterschied ist der Stand der Umsetzung.**
 *
 * Oben stehen die **Belege**: Kopf, lückenlose Nummer, Zeilen, Nullsumme —
 * seit Migration `0014` gibt es keine gelieferte Zeile mehr ohne einen. Ein
 * Storno hebt einen Beleg **ganz** auf und zeigt auf ihn (R198); beide Seiten
 * sagen es.
 *
 * Darunter stehen die **Konsolidierungsbuchungen** des Laufs, und sie tragen
 * noch keinen Beleg. Der Grund ist kein Versehen: Ein Beleg je *Fall* ist
 * richtig, einer je *Vorgang* wäre falsch (2.14, Abschnitt 3.2) — und welche
 * Zeilen einen Fall bilden, sagt heute nur der Rechenkern und sagt es noch
 * nicht. Bis dahin klammert der Vorgang, und das Journal schreibt es hin,
 * statt so zu tun, als sei es schon so weit (R144).
 */

/**
 * Worauf ein Link aus dem Wertenachweis zeigt (R209).
 *
 * **Beleg oder Vorgang, je nach Herkunft:** Was geliefert wurde, hängt seit
 * Migration `0014` an einem Beleg; was der Lauf gerechnet hat, klammert bis auf
 * Weiteres sein Vorgang (2.14, Abschnitt 3.2).
 */
export type Journalziel = {
  lauf?: number
  vorgang?: number
  beleg?: number
  konto?: string
  einheit?: string
}

/** Wie viele Belege die Ansicht höchstens zeigt — über alle Perioden die jüngsten (R986). */
const GRENZE = 200

const BELEGART: Record<string, Sprachschluessel> = {
  lieferung: 'journal.art.lieferung',
  erfassung: 'journal.art.erfassung',
  vortrag: 'journal.art.vortrag',
  umrechnung: 'journal.art.umrechnung',
  konsolidierung: 'journal.art.konsolidierung',
  storno: 'journal.art.storno',
}

export function Buchungsjournal({
  lauf,
  ledger,
  periode,
  ziel,
  aufZielGeloest,
}: {
  lauf: Rechenantwort | null
  ledger: string
  /** Die Periode des Journals — `0` heißt: alle (R986). */
  periode: number
  /** Aus dem Wertenachweis mitgebracht — die Zeile, die gemeint ist. */
  ziel?: Journalziel
  /** Der Filter ist gelöst worden; die Adresse soll ihn nicht mehr tragen. */
  aufZielGeloest: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('buchungsjournal:buchungsjournal')

  const [belege, setBelege] = useState<Belegkopf[] | null>(null)
  /** Ob die Grenze von 200 Belegen erreicht ist — dann sagt es die Seite (R986). */
  const [begrenzt, setBegrenzt] = useState(false)
  const [buchungen, setBuchungen] = useState<Buchungszeile[] | null>(null)
  const [vorgaenge, setVorgaenge] = useState<Vorgangskopf[]>([])
  const [fehler, setFehler] = useState(false)
  /**
   * Was dazu am Ort steht — nur ein fachlicher Satz; eine Störung meldet das
   * Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
   */
  const [fehlersatz, setFehlersatz] = useState<string | null>(null)
  const [suche, setSuche] = useState('')
  const gezielt = useRef<HTMLTableRowElement | null>(null)

  // **Der Lauf des Links sticht den der Kontextzeile.** Wer aus dem Nachweis
  // eines Anfangsbestands kommt, will den Beleg sehen, der ihn gebucht hat —
  // und der liegt im Vorjahreslauf.
  const laufId = ziel?.lauf ?? lauf?.laufId ?? null
  const fremderLauf = laufId !== null && lauf !== null && laufId !== lauf.laufId
  const zielhaft =
    ziel !== undefined &&
    (ziel.vorgang !== undefined || ziel.beleg !== undefined || ziel.konto !== undefined)

  // Die Belege der eingestellten Periode. Sie hängen an Bewertungsebene und Periode und
  // nicht am Lauf: Ein Beleg ist da, ob jemand gerechnet hat oder nicht.
  useEffect(() => {
    // Ohne Periode alle Perioden, die jüngsten zuerst (R986).
    if (!ledger) {
      setBelege(null)
      return
    }

    let gilt = true
    setBelege(null)

    /*
      **Der verlinkte Beleg wird zusätzlich geholt.** Er liegt oft nicht in der
      eingestellten Periode: Der Anfangsbestand eines Nachweises kommt aus dem
      Vorjahr (R206), und sein Beleg auch. Ihn dann nicht zu finden, weil der
      Periodenfilter dazwischensteht, wäre genau der Fehler, den R209 verbietet
      — der Link führt an die Stelle, nicht in ihre Nähe.
    */
    Promise.all([
      api.journal({ ledger, periode: periode || undefined, konto: ziel?.konto, hoechstens: GRENZE }),
      ziel?.beleg === undefined ? Promise.resolve([]) : api.journal({ beleg: ziel.beleg }),
    ])
      .then(([derPeriode, verlinkt]) => {
        if (!gilt) {
          return
        }

        const fehlt = verlinkt.filter((v) => !derPeriode.some((b) => b.id === v.id))
        setBelege([...fehlt, ...derPeriode])
        setBegrenzt(derPeriode.length >= GRENZE)
      })
      .catch((f) => {
        if (gilt) {
          setFehler(true)
          setFehlersatz(fachlicherSatz(f))
        }
      })

    return () => {
      gilt = false
    }
  }, [ledger, periode, ziel?.konto, ziel?.beleg])

  useEffect(() => {
    if (laufId === null) {
      setBuchungen(null)
      setVorgaenge([])
      return
    }

    let gilt = true
    setBuchungen(null)

    Promise.all([api.vorgaenge(laufId), api.buchungen(laufId)])
      .then(([v, b]) => {
        if (gilt) {
          setVorgaenge(v)
          setBuchungen(b)
        }
      })
      .catch((f) => {
        if (gilt) {
          setFehler(true)
          setFehlersatz(fachlicherSatz(f))
        }
      })

    return () => {
      gilt = false
    }
  }, [laufId])

  // **Die Zeile, auf die der Link zeigt, ist sofort sichtbar** (2.14, Abschnitt
  // 10): Niemand blättert durch dreitausend Zeilen zu seiner.
  useEffect(() => {
    gezielt.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [buchungen, belege, ziel?.vorgang, ziel?.konto, ziel?.einheit])

  const text = suche.trim().toLowerCase()

  function passt(b: Buchungszeile): boolean {
    if (!text) {
      return true
    }

    return (
      b.konto.toLowerCase().includes(text) ||
      kontobezeichnung(b.konto).toLowerCase().includes(text) ||
      (b.einheit ?? '').toLowerCase().includes(text) ||
      // Die Zelle zeigt den Namen (R838), also findet die Suche ihn auch.
      (b.einheit ? gesellschaftsname(b.einheit) : '').toLowerCase().includes(text) ||
      String(b.buchungsnummer) === text
    )
  }

  const gefiltert = (buchungen ?? []).filter(passt)

  const getroffen = zielhaft
    ? gefiltert.find(
        (b) =>
          (ziel?.vorgang === undefined || ziel.vorgang === b.vorgangId) &&
          (ziel?.konto === undefined || ziel.konto === b.konto) &&
          (ziel?.einheit === undefined || ziel.einheit === b.einheit),
      )
    : undefined

  const gezeigteBelege = (belege ?? []).filter(
    (b) =>
      !text ||
      b.nummer.toLowerCase().includes(text) ||
      (b.einheit ?? '').toLowerCase().includes(text) ||
      (b.einheit ? gesellschaftsname(b.einheit) : '').toLowerCase().includes(text),
  )

  return (
    <>
      <div className="kennzahlen">
        <Kennzeichen name={t('journal.periode')}>
          {periode ? periodenname(periode) : t('journal.periode.alle')}
        </Kennzeichen>
        {/*
          **Der Lauf heißt nach seinem Zeitpunkt** (7.2 Abschnitt 36, R1142),
          und die Zahl der Belege und Buchungen steht nicht mehr da (R1143 —
          eine Menge ohne Entscheidungswert). Ein Lauf aus einem Verweis ist
          eine frühere Rechnung; warum er gezeigt wird, sagt der Satz darunter.
        */}
        <Kennzeichen name={t('journal.lauf')}>
          {fremderLauf ? t('journal.lauf.frueher') : lauf && laufId !== null ? zeitpunkt(lauf.gerechnetAm) : '—'}
        </Kennzeichen>
      </div>

      <div className="berichtsleiste">
        <div className="feld">
          <label htmlFor="journalsuche">{t('journal.suche')}</label>
          <input
            id="journalsuche"
            type="search"
            value={suche}
            placeholder={t('journal.suche.platzhalter')}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>

        {/*
          **Der Filter, mit dem der Anwender hereinkam, steht sichtbar und ist
          mit einem Griff zu lösen** (2.14, Abschnitt 7) — sonst sucht er später
          im halben Journal und wundert sich, warum sein Beleg fehlt.
        */}
        {zielhaft && (
          <p className="herkunftsfilter">
            {t('journal.filter.aktiv', {
              // Der Beleg steht mit seiner Nummer da, wo es einen gibt; sonst
              // mit dem Vorgang, der ihn bis auf Weiteres vertritt.
              beleg:
                ziel?.beleg !== undefined
                  ? ((belege ?? []).find((b) => b.id === ziel.beleg)?.nummer ?? String(ziel.beleg))
                  : ziel?.vorgang !== undefined
                    ? belegname(vorgaenge, ziel.vorgang)
                    : '—',
              konto: ziel?.konto ? kontobezeichnung(ziel.konto) : '—',
            })}{' '}
            <button type="button" className="schmal" onClick={aufZielGeloest}>
              {t('journal.filter.loesen')}
            </button>
          </p>
        )}
      </div>

      {fehlersatz && <p className="hinweis">{fehlersatz}</p>}

      {/* ---- Die Belege ------------------------------------------------- */}

      <h2 className="journalabschnitt">{t('journal.abschnitt.belege')}</h2>

      {/* Über alle Perioden stehen die jüngsten 200 — und die Seite sagt es (R986). */}
      {begrenzt && (
        <p className="herkunftsfilter">{t('journal.grenze', { anzahl: anzahlwort(GRENZE) })}</p>
      )}

      {/*
        **Die Überschrift sagt „dieser Periode", und der verlinkte Beleg ist es
        oft nicht** — der Anfangsbestand kommt aus dem Vorjahr (R206). Er steht
        trotzdem oben; ihn wegzulassen hieße, den Link ins Leere laufen zu
        lassen. Also steht daneben, warum er da ist (R144).
      */}
      {(() => {
        const fremd = (belege ?? []).find(
          (b) => b.id === ziel?.beleg && periode !== 0 && b.periode !== periode,
        )

        return fremd === undefined ? null : (
          <p className="herkunftsfilter">
            {t('journal.fremdeperiode', {
              nummer: fremd.nummer,
              periode: periodenname(fremd.periode),
            })}
          </p>
        )
      })()}

      {belege === null && !fehler && <p className="hinweis">{t('vorgang.laedt')}</p>}
      {belege !== null && gezeigteBelege.length === 0 && (
        <p className="hinweis">{t('journal.keine.belege')}</p>
      )}

      {gezeigteBelege.map((beleg) => (
        <Beleg
          key={beleg.id}
          beleg={beleg}
          offen={text.length > 0 || ziel?.beleg === beleg.id}
          gemeint={ziel?.beleg === beleg.id}
        />
      ))}

      {/* ---- Was noch keinen Beleg trägt --------------------------------- */}

      <h2 className="journalabschnitt">{t('journal.abschnitt.ohnebeleg')}</h2>
      <p className="erlaeuterung">{t('journal.was.fehlt')}</p>

      {fremderLauf && <p className="herkunftsfilter">{t('journal.fremderlauf')}</p>}
      {laufId === null && <p className="hinweis">{t('journal.keinlauf')}</p>}
      {laufId !== null && buchungen === null && !fehler && (
        <p className="hinweis">{t('vorgang.laedt')}</p>
      )}

      {buchungen !== null &&
        vorgaenge.map((vorgang) => {
          const zeilen = gefiltert.filter((b) => b.vorgangId === vorgang.id)
          if (zeilen.length === 0) {
            return null
          }

          const gemeint = ziel?.vorgang === vorgang.id
          const offen = gemeint || (!zielhaft && text.length > 0)

          return (
            <details key={vorgang.id} className="vorgang" open={offen}>
              <summary>
                <span className="nummer">{vorgang.reihenfolge}</span>
                <span className="name">{vorgangsname(vorgang)}</span>
                {/* Ohne Zahl der Buchungen (7.2 Abschnitt 36, R1143). */}
              </summary>

              <div style={{ padding: '10px 12px' }}>
                <div className="tabellenrahmen">
                  <table ref={spaltenrahmen} className="ziehbaretabelle">
                    <Spaltenmassleiste masse={spaltenmasse} />
                    <thead>
                      <tr>
                        <th>{t('vorgang.spalte.nummer')}</th>
                        <th>{t('vorgang.spalte.einheit')}</th>
                        <th>{t('vorgang.spalte.konto')}</th>
                        <th className="zahl">{t('vorgang.spalte.betrag')}</th>
                        <th>{t('vorgang.spalte.regel')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zeilen.map((buchung) => {
                        const trifft = buchung === getroffen

                        return (
                          <tr
                            key={buchung.id}
                            ref={trifft ? gezielt : undefined}
                            className={trifft ? 'trefferaktiv' : undefined}
                          >
                            <td className="zahl">{buchung.buchungsnummer}</td>
                            <td>
                              {buchung.einheit ? gesellschaftsname(buchung.einheit) : ohneWert('gesellschaft')}
                            </td>
                            <td className={trifft ? 'trefferzelle' : undefined}>
                              {kontobezeichnung(buchung.konto)}
                            </td>
                            <td className={`zahl ${buchung.betrag < 0 ? 'minus' : ''}`}>
                              <Betrag wert={buchung.betrag} />
                            </td>
                            <td>
                              <div>{regelname(buchung.regel)}</div>
                              <div className="erlaeuterung">{satz(buchung.erlaeuterung)}</div>
                              {buchung.herleitung.length > 0 && (
                                <dl className="herleitung">
                                  {buchung.herleitung.map((schritt, i) => (
                                    <div key={i} style={{ display: 'contents' }}>
                                      <dt>{herleitungsname(schritt.feld)}</dt>
                                      <dd>{schrittwert(schritt)}</dd>
                                    </div>
                                  ))}
                                </dl>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Kein Prüfcode am einzelnen Schritt (7.2 Abschnitt 37, R1147). */}
              </div>
            </details>
          )
        })}
    </>
  )
}

/**
 * Ein Beleg mit seinen Zeilen.
 *
 * Die Zeilen werden erst beim Aufklappen geholt — ein Konzernbeleg hat
 * Tausende, und niemand will sie alle sehen, nur die eines Belegs.
 */
function Beleg({
  beleg,
  offen,
  gemeint,
}: {
  beleg: Belegkopf
  offen: boolean
  /** Auf diesen Beleg zeigt der Link aus dem Wertenachweis (R209). */
  gemeint?: boolean
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('buchungsjournal:beleg')

  const [zeilen, setZeilen] = useState<Belegzeile[] | null>(null)
  const [laedt, setLaedt] = useState(false)
  const rahmen = useRef<HTMLDetailsElement | null>(null)

  // **Der gemeinte Beleg ist sofort sichtbar** (2.14, Abschnitt 10). Niemand
  // blättert durch zweihundert Belege zu seinem.
  useEffect(() => {
    if (gemeint) {
      rahmen.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [gemeint])

  async function aufklappen(e: React.SyntheticEvent<HTMLDetailsElement>) {
    if (!e.currentTarget.open || zeilen !== null || laedt) {
      return
    }

    setLaedt(true)
    try {
      // Ohne Vortragszeilen (R1063): Sie heben sich im Beleg auf.
      setZeilen(ohneVortragszeilen(await api.belegzeilen(beleg.id)))
    } finally {
      setLaedt(false)
    }
  }

  const einheit = beleg.einheit ? gesellschaftsname(beleg.einheit) : ohneWert('gesellschaft')
  // Die Erläuterung nur, wo die Zeile darüber den Namen trägt — und ohne die
  // Zeilenzahl des Datenimports (R1063).
  const erlaeuterung = beleg.name?.trim() ? belegerlaeuterung(beleg.erlaeuterung, beleg.name) : null

  return (
    <details
      className={`vorgang beleg${gemeint ? ' gemeint' : ''}`}
      open={offen}
      onToggle={aufklappen}
      ref={rahmen}
    >
      <summary>
        <span className="belegnummer">{beleg.nummer}</span>
        <span className={`belegart ${beleg.belegart}`}>
          {BELEGART[beleg.belegart] ? t(BELEGART[beleg.belegart]!) : beleg.belegart}
        </span>

        {/*
          **Der Name steht in der Zeile, nicht erst im aufgeschlagenen Beleg**
          (R374). Die Liste zeigte bisher nur die Nummer — und eine Nummer sagt
          nicht, worum es geht: Wer *„Abgrenzung Wartungsvertrag"* sucht, musste
          zwanzig Belege aufklappen. Der Belegordner führt den Namen längst als
          Hauptspalte; die chronologische Liste hinkte hinterher.

          **Eine Lieferung hat keinen Namen** — niemand legt sie von Hand ab.
          Dort steht ihre Erläuterung („Monatsmeldung NL-FR 09/2026"): dieselbe
          Frage, andere Antwort. Sie steht dann NICHT noch einmal im
          aufgeschlagenen Beleg; zweimal derselbe Satz untereinander ist kein
          Nachweis, sondern Füllung.
        */}
        {/*
          Der gespeicherte Text wird mit Namen gelesen, nicht umgeschrieben
          (R838) — und mit Monat und Jahr (7.2 Abschnitt 21, R839): In dieser
          einen Zeile stand dieselbe Periode dreimal verschieden, „12/2026" im
          Namen, „202612" in der Erläuterung, „Dezember 2026" in der Spalte.
        */}
        <span className="name">{belegtext(beleg.name?.trim() || beleg.erlaeuterung)}</span>

        <span className="nummer">{einheit}</span>
        {/*
          Die Zeilenzahl stand hier bis zum 2026-10-05 — sie beantwortet keine
          Frage des Anwenders (Auftrag vom 2026-10-05, R1063).
        */}
        <span className="nummer"><Betrag wert={beleg.summe} /></span>
      </summary>

      <div style={{ padding: '10px 12px' }}>
        {/*
          Die Erläuterung nur, wo die Zeile darüber den NAMEN trägt — sonst
          stünde sie zweimal. Die Begründung steht immer: Sie ist die Antwort
          auf eine andere Frage (warum, nicht was).
        */}
        {(erlaeuterung || beleg.begruendung) && (
          <p className="erlaeuterung" style={{ marginTop: 0 }}>
            {erlaeuterung ?? ''}
            {beleg.begruendung
              ? `${erlaeuterung ? ' — ' : ''}${beleg.begruendung}`
              : ''}
          </p>
        )}

        {/*
          **Beide Seiten eines Stornos sagen es** (R198): der Stornobeleg, wen
          er aufhebt, und der aufgehobene, dass er nicht mehr gilt. Ein Beleg,
          der stillschweigend storniert ist, ist die eine Auskunft, die
          schlimmer wäre als keine.
        */}
        {beleg.stornierteNummer && (
          <p className="herkunftsfilter">
            {t('journal.hebt.auf', { nummer: beleg.stornierteNummer })}
          </p>
        )}
        {beleg.storniertDurch && (
          <p className="herkunftsfilter">
            {t('journal.storniert.durch', { nummer: beleg.storniertDurch })}
          </p>
        )}

        {laedt && <p className="hinweis">{t('vorgang.laedt')}</p>}

        {zeilen !== null && (
          <div className="tabellenrahmen">
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                <tr>
                  <th>{t('vorgang.spalte.nummer')}</th>
                  <th>{t('vorgang.spalte.einheit')}</th>
                  <th>{t('vorgang.spalte.konto')}</th>
                  <th>{t('journal.spalte.bewegungsart')}</th>
                  <th>{t('journal.spalte.partner')}</th>
                  <th className="zahl">{t('vorgang.spalte.betrag')}</th>
                </tr>
              </thead>
              <tbody>
                {/*
                  **Fortlaufend gezählt, ohne Lücke** (R1063): Die gespeicherte
                  Zeilennummer sprang über die ausgeblendeten Vortragszeilen —
                  2, 4, 7, 9 … —, und die Lücke fragte, was fehlt.
                */}
                {zeilen.map((z, i) => (
                  <tr key={z.id}>
                    <td className="zahl">{i + 1}</td>
                    <td>{gesellschaftsname(z.einheit)}</td>
                    <td>{kontobezeichnung(z.konto)}</td>
                    <td>{bewegungsartname(z.bewegungsart)}</td>
                    <td>
                      {z.icPartner ? gesellschaftsname(z.icPartner) : ohneWert('weitere')}
                    </td>
                    <td className={`zahl ${z.betrag < 0 ? 'minus' : ''}`}>
                      <Betrag wert={z.betrag} waehrung={z.waehrung} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/*
          Die Nullsumme ist keine Zierde, sondern die Bedingung, unter der
          dieser Beleg überhaupt geschrieben werden durfte (R195). Sie
          gehört deshalb sichtbar unter seine Zeilen.
        */}
        <p className="probe stimmt" style={{ marginTop: '10px' }}>
          {t('journal.nullsumme')}
        </p>
      </div>
    </details>
  )
}

function belegname(vorgaenge: Vorgangskopf[], id: number): string {
  const vorgang = vorgaenge.find((v) => v.id === id)
  return vorgang ? vorgangsname(vorgang) : String(id)
}

function Kennzeichen({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="kennzahl">
      <span className="name">{name}</span>
      <span className="wert">{children}</span>
    </div>
  )
}
